import type { PlayerGameAction } from './PlayerGameActions'
import { PlayerGameActionTypes } from './PlayerGameActions'
import type { HandPhase } from './DomainEvent'
import { ActionType as GameplayActionType } from '@vgw/pok-gameplay-protocol'
import type { PlayerGameState, PlayerGamesState } from './PlayerGameState'

const MAX_HAND_HISTORY = 10

function createEmptyGameState(): PlayerGameState {
	return {
		hand: {
			phase: 'waiting',
			isMyTurn: false,
		},
		holeCards: {},
		communityCards: [],
		players: {},
		tableInfo: {},
		currentHandEvents: [],
		currentHandContext: { playerNames: {}, playerToSeat: {}, seatToPlayer: {} },
		handHistory: [],
	}
}

function ensureGame(state: PlayerGamesState, tableId: string): PlayerGameState {
	return state[tableId] ?? createEmptyGameState()
}

export function playerGamesReducer(
	state: PlayerGamesState = {},
	action: PlayerGameAction,
): PlayerGamesState {
	if (!action.payload || typeof action.payload !== 'object' || !('tableId' in action.payload)) {
		return state
	}

	const tableId = String(action.payload.tableId)
	const game = ensureGame(state, tableId)

	switch (action.type) {
		case PlayerGameActionTypes.INITIALIZE_GAME:
			return {
				...state,
				[tableId]: {
					...game,
					gameInfo: {
						mode: action.payload.mode,
						currency: action.payload.currency,
						variant: action.payload.variant,
						capacity: action.payload.capacity,
					},
					tableInfo: {
						...game.tableInfo,
						blinds: action.payload.blinds,
					},
				},
			}

		case PlayerGameActionTypes.UPDATE_HAND_STATE: {
			const isNewHand = action.payload.hand.phase === 'preflop' && action.payload.hand.handId != null
			if (isNewHand) {
				const resetPlayers: typeof game.players = {}
				for (const [seatId, player] of Object.entries(game.players)) {
					const { isFolded, lastAction, lastActionAmount, handStrength, ...identity } = player
					resetPlayers[Number(seatId)] = identity
				}
				// Only archive if a real hand was in progress (not pre-hand setup events)
				const hadActiveHand = game.currentHandEvents.length > 0 && game.hand.handId != null
				const archivedHistory = hadActiveHand
					? [...game.handHistory, { events: game.currentHandEvents, context: game.currentHandContext }].slice(-MAX_HAND_HISTORY)
					: game.handHistory
				return {
					...state,
					[tableId]: {
						...game,
						hand: {
							phase: 'preflop',
							isMyTurn: false,
							handId: action.payload.hand.handId,
							dealerSeatId: action.payload.hand.dealerSeatId,
						},
						holeCards: {},
						communityCards: [],
						players: resetPlayers,
						tableInfo: {
							...game.tableInfo,
							potSize: undefined,
						},
						handResult: undefined,
						currentHandEvents: [],
						currentHandContext: action.payload.context ?? game.currentHandContext,
						handHistory: archivedHistory,
					},
				}
			}
			return {
				...state,
				[tableId]: {
					...game,
					hand: {
						...game.hand,
						...action.payload.hand,
					},
				},
			}
		}

		case PlayerGameActionTypes.UPDATE_HOLE_CARDS:
			return {
				...state,
				[tableId]: {
					...game,
					holeCards: {
						...game.holeCards,
						...action.payload.cards,
					},
				},
			}

		case PlayerGameActionTypes.UPDATE_COMMUNITY_CARDS: {
			const newCommunityCards = [...game.communityCards, ...action.payload.cards]
			const phaseByCardCount: Record<number, HandPhase> = { 3: 'flop', 4: 'turn', 5: 'river' }
			const derivedPhase = phaseByCardCount[newCommunityCards.length]
			return {
				...state,
				[tableId]: {
					...game,
					communityCards: newCommunityCards,
					...(derivedPhase ? { hand: { ...game.hand, phase: derivedPhase } } : {}),
				},
			}
		}

		case PlayerGameActionTypes.UPDATE_PLAYER_INFO: {
			const { tableId: _, seatId, ...info } = action.payload
			return {
				...state,
				[tableId]: {
					...game,
					players: {
						...game.players,
						[seatId]: {
							...game.players[seatId],
							...info,
						},
					},
				},
			}
		}

		case PlayerGameActionTypes.UPDATE_PLAYER_ACTION: {
			const seat = action.payload.seatId
			if (!game.players[seat]) return state
			const isFold = action.payload.action === GameplayActionType.FOLD
			return {
				...state,
				[tableId]: {
					...game,
					players: {
						...game.players,
						[seat]: {
							...game.players[seat],
							lastAction: action.payload.action,
							lastActionAmount: action.payload.amount,
							...(isFold ? { isFolded: true } : {}),
						},
					},
				},
			}
		}

		case PlayerGameActionTypes.UPDATE_PLAYER_BALANCE: {
			const seat = action.payload.seatId
			if (!game.players[seat]) return state
			return {
				...state,
				[tableId]: {
					...game,
					players: {
						...game.players,
						[seat]: {
							...game.players[seat],
							balance: action.payload.balance,
						},
					},
				},
			}
		}

		case PlayerGameActionTypes.REMOVE_PLAYER: {
			const seatEntry = Object.entries(game.players)
				.find(([_, player]) => player.playerId === action.payload.playerId)
			if (!seatEntry) return state
			const seatKey = Number(seatEntry[0])
			const remainingPlayers = { ...game.players }
			delete remainingPlayers[seatKey]
			return {
				...state,
				[tableId]: {
					...game,
					players: remainingPlayers,
					...(seatKey === game.mySeatId ? { mySeatId: undefined } : {}),
				},
			}
		}

		case PlayerGameActionTypes.UPDATE_HAND_STRENGTH: {
			const seat = action.payload.seatId
			if (!game.players[seat]) return state
			return {
				...state,
				[tableId]: {
					...game,
					players: {
						...game.players,
						[seat]: {
							...game.players[seat],
							handStrength: action.payload.handType,
						},
					},
				},
			}
		}

		case PlayerGameActionTypes.UPDATE_TABLE_INFO:
			return {
				...state,
				[tableId]: {
					...game,
					tableInfo: {
						...game.tableInfo,
						...action.payload.tableInfo,
					},
				},
			}

		case PlayerGameActionTypes.UPDATE_MY_SEAT:
			return {
				...state,
				[tableId]: {
					...game,
					mySeatId: action.payload.seatId,
				},
			}

		case PlayerGameActionTypes.UPDATE_TOURNAMENT_INFO:
			return {
				...state,
				[tableId]: {
					...game,
					tournamentInfo: {
						...game.tournamentInfo,
						...action.payload.tournamentInfo,
					},
				},
			}

		case PlayerGameActionTypes.HAND_ENDED:
			return {
				...state,
				[tableId]: {
					...game,
					handResult: {
						hands: action.payload.hands,
						lowHands: action.payload.lowHands,
						potTransfers: action.payload.potTransfers,
						potShares: action.payload.potShares,
						winningCards: action.payload.winningCards,
						payOuts: action.payload.payOuts,
					},
				},
			}

		case PlayerGameActionTypes.RECORD_HAND_EVENT:
			return {
				...state,
				[tableId]: {
					...game,
					currentHandEvents: [
						...game.currentHandEvents,
						action.payload.domainEvent,
					],
				},
			}

		default:
			return state
	}
}
